import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Fetch lightweight list of DBQ forms for dropdowns (claims workers)
 */
export const useFetchDbqFormsList = () => {
  return useQuery({
    queryKey: ['dbq-forms-list'],
    queryFn: async () => {
      const response = await axios.get('/dbq/forms/list');
      return response.data;
    },
  });
};

/**
 * Fetch list of DBQ forms (admin)
 */
export const useFetchDbqForms = (page = 1, search = '', enabled = true) => {
  return useQuery({
    queryKey: ['dbq-forms', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100', // Get all forms for dropdown
      });
      if (search) {
        params.append('search', search);
      }
      const response = await axios.get(`/dbq/forms?${params}`);
      return response.data;
    },
    enabled,
  });
};

/**
 * Fetch a single DBQ form
 */
export const useFetchDbqForm = (formId) => {
  return useQuery({
    queryKey: ['dbq-form', formId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/forms/${formId}`);
      return response.data;
    },
    enabled: !!formId,
  });
};

/**
 * Create DocuSeal template from PDF for a DBQ form
 * Uploads PDF, extracts form field positions, and creates template via DocuSeal API
 */
export const useCreateDocuSealTemplate = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, pdfFile }) => {
      const formData = new FormData();
      formData.append('pdf', pdfFile);

      const response = await axios.post(`/dbq/forms/${dbqFormId}/create-template`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Regenerate DBQ debug artifacts.
 * If pdfFile is omitted, backend will try to use a previously stored source PDF.
 */
export const useRegenerateDbqDebugArtifacts = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, pdfFile }) => {
      if (pdfFile) {
        const formData = new FormData();
        formData.append('pdf', pdfFile);

        const response = await axios.post(`/dbq/forms/${dbqFormId}/regenerate-artifacts`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      }

      const response = await axios.post(`/dbq/forms/${dbqFormId}/regenerate-artifacts`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Calculate character limits for long_text fields based on PDF field dimensions.
 * Requires uploading the DBQ PDF to extract text field heights and calculate max char counts.
 */
export const useCalculateCharLimits = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, pdfFile }) => {
      const formData = new FormData();
      formData.append('pdf', pdfFile);

      const response = await axios.post(`/dbq/forms/${dbqFormId}/calculate-char-limits`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Calculate the character limit for the MO/Remarks field only, then truncate any
 * existing user_dbq_forms whose stored MO value exceeds the new limit. Per-form
 * scoped: targets the MO field specifically and migrates DBQs linked to this form.
 */
export const useCalculateMoCharLimit = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, pdfFile }) => {
      const formData = new FormData();
      formData.append('pdf', pdfFile);

      const response = await axios.post(`/dbq/forms/${dbqFormId}/calculate-mo-char-limit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Clear all character-limit validation (max_length, max_lines, chars_per_line)
 * from every long_text field on a DBQ template. Stateless — no PDF needed.
 * Useful as a reset before re-running Calculate Char Limits.
 */
export const useClearCharLimits = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId }) => {
      const response = await axios.post(`/dbq/forms/${dbqFormId}/clear-char-limits`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

